import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { CollaborationFeed } from "@/components/collaboration/CollaborationFeed";
import { CommentComposer } from "@/components/collaboration/CommentComposer";
import { ApprovalRequestDialog } from "@/components/collaboration/ApprovalRequestDialog";
import { CallMonitorWidget } from "@/components/call-monitor/CallMonitorWidget";
import {
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Calendar,
  MessageSquare,
  TrendingUp,
  Sparkles,
  CheckCircle,
  CheckCircle2,
  Clock,
  Lightbulb,
  Send,
  Save,
  ArrowRight,
  User,
  Target,
  Zap,
  Plus,
  Edit2,
  Activity,
  BarChart3,
  Building2,
  Briefcase,
  Flame,
  Snowflake,
  ThermometerSun,
  Users,
  Shield,
  ExternalLink,
  Linkedin,
  Globe,
  AlertTriangle,
  TrendingDown,
  Link,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface LeadDetailPanelProps {
  leadId: string;
  onClose: () => void;
  onConvert: (leadId: string) => void;
  isConverting: boolean;
}

interface Activity {
  id: string;
  type: string;
  description: string;
  created_at: string;
  metadata?: any;
}

interface CampaignEnrollment {
  id: string;
  status: string;
  current_step: number;
  next_send_at?: string;
  campaign: {
    name: string;
  };
  sequence: {
    name: string;
  };
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    new: "bg-blue-500 text-white",
    contacted: "bg-purple-500 text-white",
    qualified: "bg-green-500 text-white",
    proposal_sent: "bg-orange-500 text-white",
    negotiating: "bg-yellow-500 text-white",
    won: "bg-emerald-500 text-white",
    lost: "bg-red-500 text-white",
  };
  return colors[status] || "bg-muted";
};

const getTemperatureIcon = (temp: string) => {
  if (temp === 'hot') return <Flame className="h-4 w-4 text-red-500" />;
  if (temp === 'warm') return <ThermometerSun className="h-4 w-4 text-orange-500" />;
  return <Snowflake className="h-4 w-4 text-blue-500" />;
};

const getTemperatureColor = (temp: string) => {
  if (temp === 'hot') return "bg-red-100 text-red-700 border-red-200";
  if (temp === 'warm') return "bg-orange-100 text-orange-700 border-orange-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
};

export function LeadDetailPanel({ leadId, onClose, onConvert, isConverting }: LeadDetailPanelProps) {
  const { user, orgId } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [enrollments, setEnrollments] = useState<CampaignEnrollment[]>([]);
  const [leadScore, setLeadScore] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [savingNotes, setSavingNotes] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedLead, setEditedLead] = useState<any>({});
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  const [addingTask, setAddingTask] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [calculatingScore, setCalculatingScore] = useState(false);
  const [aiEmailPrompt, setAiEmailPrompt] = useState("");
  const [draftingWithAI, setDraftingWithAI] = useState(false);

  useEffect(() => {
    if (leadId) {
      loadLeadData();
      loadActivities();
      loadEnrollments();
      loadLeadScore();
      loadTasks();
    }
  }, [leadId]);

  useEffect(() => {
    if (lead) {
      setEditedLead({
        contact_name: lead.contact_name || "",
        contact_email: lead.contact_email || "",
        contact_phone: lead.contact_phone || "",
        property_address: lead.property_address || "",
        property_city: lead.property_city || "",
        property_state: lead.property_state || "",
        property_zip: lead.property_zip || "",
        estimated_value: lead.estimated_value || null,
        status: lead.status || "new",
        lead_source: lead.lead_source || "",
        lead_temperature: lead.lead_temperature || "cold",
        win_probability: lead.win_probability || null,
        expected_close_date: lead.expected_close_date || "",
        company_name: lead.company_name || "",
        job_title: lead.job_title || "",
        pain_points: lead.pain_points || "",
        competitors: lead.competitors || "",
        budget_qualified: lead.budget_qualified || false,
        decision_maker: lead.decision_maker || false,
        linkedin_url: lead.linkedin_url || "",
        website_url: lead.website_url || "",
      });
    }
  }, [lead]);

  const loadLeadData = async () => {
    try {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .eq("id", leadId)
        .single();

      if (error) throw error;
      setLead(data);
      setNotes(data.notes || "");
    } catch (error) {
      console.error("Error loading lead:", error);
      toast.error("Failed to load lead details");
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      const { data: emailSends } = await supabase
        .from("campaign_email_sends")
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false })
        .limit(20);

      const activities: Activity[] = [];

      emailSends?.forEach((send) => {
        activities.push({
          id: send.id,
          type: "email_sent",
          description: `Email sent: ${send.subject}`,
          created_at: send.sent_at || send.created_at,
          metadata: send,
        });

        if (send.opened_at) {
          activities.push({
            id: `${send.id}-opened`,
            type: "email_opened",
            description: `Opened email: ${send.subject}`,
            created_at: send.opened_at,
            metadata: send,
          });
        }

        if (send.clicked_at) {
          activities.push({
            id: `${send.id}-clicked`,
            type: "email_clicked",
            description: `Clicked link in email: ${send.subject}`,
            created_at: send.clicked_at,
            metadata: send,
          });
        }

        if (send.replied_at) {
          activities.push({
            id: `${send.id}-replied`,
            type: "email_replied",
            description: `Replied to: ${send.subject}`,
            created_at: send.replied_at,
            metadata: { ...send, reply_body: send.reply_body },
          });
        }
      });

      activities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setActivities(activities);
    } catch (error) {
      console.error("Error loading activities:", error);
    }
  };

  const loadEnrollments = async () => {
    try {
      const { data } = await supabase
        .from("campaign_enrollments")
        .select(`
          *,
          campaign:ai_campaigns(name),
          sequence:email_sequences(name)
        `)
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      setEnrollments(data || []);
    } catch (error) {
      console.error("Error loading enrollments:", error);
    }
  };

  const loadLeadScore = async () => {
    try {
      const { data } = await supabase
        .from("lead_scores")
        .select("*")
        .eq("lead_id", leadId)
        .maybeSingle();

      setLeadScore(data);
    } catch (error) {
      console.error("Error loading lead score:", error);
    }
  };

  const loadTasks = async () => {
    try {
      const { data } = await supabase
        .from("tasks")
        .select("*")
        .eq("lead_id", leadId)
        .order("due_date", { ascending: true })
        .limit(10);

      setTasks(data || []);
    } catch (error) {
      console.error("Error loading tasks:", error);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const { error } = await supabase
        .from("leads")
        .update({ notes, updated_at: new Date().toISOString() })
        .eq("id", leadId);

      if (error) throw error;
      toast.success("Notes saved");
      setEditingNotes(false);
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveLead = async () => {
    try {
      // Convert empty strings to null for numeric fields
      const sanitizedLead = {
        ...editedLead,
        estimated_value: editedLead.estimated_value === "" || editedLead.estimated_value === null ? null : parseFloat(editedLead.estimated_value) || null,
        win_probability: editedLead.win_probability === "" || editedLead.win_probability === null ? null : parseInt(editedLead.win_probability) || null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from("leads")
        .update(sanitizedLead)
        .eq("id", leadId);

      if (error) throw error;
      toast.success("Lead updated successfully");
      setEditMode(false);
      await loadLeadData();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) {
      toast.error("Task title is required");
      return;
    }
    setAddingTask(true);
    try {
      const { error } = await supabase.from("tasks").insert({
        lead_id: leadId,
        org_id: orgId,
        title: newTaskTitle,
        due_date: newTaskDueDate || null,
        priority: newTaskPriority,
        status: "pending",
      });

      if (error) throw error;
      toast.success("Task created");
      setNewTaskTitle("");
      setNewTaskDueDate("");
      setNewTaskPriority("medium");
      await loadTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setAddingTask(false);
    }
  };

  const handleCalculateScore = async () => {
    setCalculatingScore(true);
    try {
      const { data, error } = await supabase.functions.invoke("calculate-lead-score", {
        body: { leadId },
      });

      if (error) throw error;
      toast.success("Lead score calculated");
      await loadLeadScore();
    } catch (error) {
      console.error("Error calculating score:", error);
      toast.error("Failed to calculate lead score");
    } finally {
      setCalculatingScore(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSendingEmail(true);
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          to: lead.contact_email,
          subject: emailSubject,
          html: emailBody.replace(/\n/g, "<br>"),
          leadId,
        },
      });

      if (error) throw error;
      
      // Update last contact date and count
      await supabase
        .from("leads")
        .update({ 
          last_contact_date: new Date().toISOString(),
          contact_count: (lead.contact_count || 0) + 1
        })
        .eq("id", leadId);

      toast.success("Email sent successfully");
      setEmailDialogOpen(false);
      setEmailSubject("");
      setEmailBody("");
      await loadActivities();
      await loadLeadData();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from("tasks")
        .update({ status: "completed" })
        .eq("id", taskId);

      if (error) throw error;
      toast.success("Task marked as complete");
      await loadTasks();
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error("Failed to complete task");
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "email_sent":
        return <Send className="h-4 w-4 text-blue-500" />;
      case "email_opened":
        return <Mail className="h-4 w-4 text-green-500" />;
      case "email_clicked":
        return <Zap className="h-4 w-4 text-purple-500" />;
      case "email_replied":
        return <MessageSquare className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 60) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    if (score >= 40) return "text-orange-600 bg-orange-50 border-orange-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getPriorityColor = (priority: string) => {
    if (priority === 'high') return "text-red-600 bg-red-50";
    if (priority === 'medium') return "text-orange-600 bg-orange-50";
    return "text-blue-600 bg-blue-50";
  };

  if (loading || !lead) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-muted-foreground">Loading lead details...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Premium Header */}
      <div className="p-6 border-b bg-card space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-3">
            {/* Name & Status Row */}
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                {!editMode ? (
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold">{lead.contact_name || "Unnamed Lead"}</h2>
                    <Badge className={cn(getStatusColor(lead.status))}>
                      {lead.status?.replace("_", " ").toUpperCase()}
                    </Badge>
                    <Badge className={cn("border", getTemperatureColor(lead.lead_temperature || 'cold'))}>
                      {getTemperatureIcon(lead.lead_temperature || 'cold')}
                      <span className="ml-1">{(lead.lead_temperature || 'cold').toUpperCase()}</span>
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => setEditMode(true)}>
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={editedLead.contact_name}
                      onChange={(e) => setEditedLead({ ...editedLead, contact_name: e.target.value })}
                      placeholder="Contact Name"
                    />
                    <div className="flex gap-2">
                      <Select
                        value={editedLead.status}
                        onValueChange={(v) => setEditedLead({ ...editedLead, status: v })}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                          <SelectItem value="negotiating">Negotiating</SelectItem>
                          <SelectItem value="won">Won</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={editedLead.lead_temperature}
                        onValueChange={(v) => setEditedLead({ ...editedLead, lead_temperature: v })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hot">🔥 Hot</SelectItem>
                          <SelectItem value="warm">☀️ Warm</SelectItem>
                          <SelectItem value="cold">❄️ Cold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                {lead.company_name && !editMode && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <Building2 className="h-3 w-3" />
                    {lead.company_name}
                    {lead.job_title && (
                      <>
                        <span>•</span>
                        <Briefcase className="h-3 w-3" />
                        {lead.job_title}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Property Info */}
            {!editMode && lead.property_address && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {lead.property_address}
                {lead.property_city && `, ${lead.property_city}`}
                {lead.property_state && ` ${lead.property_state}`}
                {lead.property_zip && ` ${lead.property_zip}`}
              </div>
            )}
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-5 gap-3">
          <Card className="p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <DollarSign className="h-3 w-3" />
                Deal Value
              </div>
              {!editMode ? (
                <div className="text-lg font-bold">
                  ${(lead.estimated_value || 0).toLocaleString()}
                </div>
              ) : (
                <Input
                  type="number"
                  value={editedLead.estimated_value}
                  onChange={(e) => setEditedLead({ ...editedLead, estimated_value: e.target.value })}
                  className="h-8"
                />
              )}
            </div>
          </Card>

          <Card className="p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3" />
                Win Probability
              </div>
              {!editMode ? (
                <div className="text-lg font-bold">{lead.win_probability || 0}%</div>
              ) : (
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editedLead.win_probability}
                  onChange={(e) => setEditedLead({ ...editedLead, win_probability: e.target.value })}
                  className="h-8"
                />
              )}
            </div>
          </Card>

          <Card className="p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Target className="h-3 w-3" />
                Lead Source
              </div>
              {!editMode ? (
                <div className="text-sm font-semibold truncate">{lead.lead_source || "Unknown"}</div>
              ) : (
                <Input
                  value={editedLead.lead_source}
                  onChange={(e) => setEditedLead({ ...editedLead, lead_source: e.target.value })}
                  placeholder="e.g., Website, Referral"
                  className="h-8"
                />
              )}
            </div>
          </Card>

          <Card className="p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                Expected Close
              </div>
              {!editMode ? (
                <div className="text-sm font-semibold">
                  {lead.expected_close_date 
                    ? new Date(lead.expected_close_date).toLocaleDateString()
                    : "Not set"}
                </div>
              ) : (
                <Input
                  type="date"
                  value={editedLead.expected_close_date}
                  onChange={(e) => setEditedLead({ ...editedLead, expected_close_date: e.target.value })}
                  className="h-8"
                />
              )}
            </div>
          </Card>

          <Card className="p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Activity className="h-3 w-3" />
                Last Contact
              </div>
              <div className="text-sm font-semibold">
                {lead.last_contact_date 
                  ? new Date(lead.last_contact_date).toLocaleDateString()
                  : "Never"}
              </div>
              <div className="text-xs text-muted-foreground">
                {lead.contact_count || 0} touch{(lead.contact_count || 0) !== 1 ? 'es' : ''}
              </div>
            </div>
          </Card>
        </div>

        {/* Call with Bridgette Widget */}
        <div className="px-1">
          <CallMonitorWidget leadId={leadId} mode="lead" />
        </div>

        {/* Contact & Qualification Info */}
        {!editMode ? (
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-muted-foreground mb-2">CONTACT INFO</div>
                {lead.contact_email && (
                  <button 
                    onClick={() => {
                      sessionStorage.setItem('email_compose_to', lead.contact_email);
                      navigate('/email');
                    }}
                    className="flex items-center gap-2 text-sm hover:underline"
                  >
                    <Mail className="h-3 w-3 text-muted-foreground" />
                    {lead.contact_email}
                  </button>
                )}
                {lead.contact_phone && (
                  <a href={`tel:${lead.contact_phone}`} className="flex items-center gap-2 text-sm hover:underline">
                    <Phone className="h-3 w-3 text-muted-foreground" />
                    {lead.contact_phone}
                  </a>
                )}
                {lead.linkedin_url && (
                  <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline text-blue-600">
                    <Linkedin className="h-3 w-3" />
                    LinkedIn Profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {lead.website_url && (
                  <a href={lead.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline text-blue-600">
                    <Globe className="h-3 w-3" />
                    Website
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </Card>

            <Card className="p-3">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-muted-foreground mb-2">QUALIFICATION</div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Shield className="h-3 w-3" />
                    Decision Maker
                  </span>
                  <Badge variant={lead.decision_maker ? "default" : "secondary"}>
                    {lead.decision_maker ? "Yes" : "No"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <DollarSign className="h-3 w-3" />
                    Budget Qualified
                  </span>
                  <Badge variant={lead.budget_qualified ? "default" : "secondary"}>
                    {lead.budget_qualified ? "Yes" : "No"}
                  </Badge>
                </div>
                {leadScore && (
                  <div className="flex items-center justify-between text-sm pt-1 border-t">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="h-3 w-3" />
                      Lead Score
                    </span>
                    <Badge className={cn("font-bold", getScoreColor(leadScore.overall_score))}>
                      {leadScore.overall_score}/100
                    </Badge>
                  </div>
                )}
              </div>
            </Card>
          </div>
        ) : (
          <div className="space-y-3 border-t pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editedLead.contact_email}
                  onChange={(e) => setEditedLead({ ...editedLead, contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={editedLead.contact_phone}
                  onChange={(e) => setEditedLead({ ...editedLead, contact_phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={editedLead.company_name}
                  onChange={(e) => setEditedLead({ ...editedLead, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input
                  value={editedLead.job_title}
                  onChange={(e) => setEditedLead({ ...editedLead, job_title: e.target.value })}
                />
              </div>
              <div>
                <Label>LinkedIn URL</Label>
                <Input
                  value={editedLead.linkedin_url}
                  onChange={(e) => setEditedLead({ ...editedLead, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <Label>Website URL</Label>
                <Input
                  value={editedLead.website_url}
                  onChange={(e) => setEditedLead({ ...editedLead, website_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 border rounded">
                <Label htmlFor="decision-maker">Decision Maker</Label>
                <Switch
                  id="decision-maker"
                  checked={editedLead.decision_maker}
                  onCheckedChange={(checked) => setEditedLead({ ...editedLead, decision_maker: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <Label htmlFor="budget-qualified">Budget Qualified</Label>
                <Switch
                  id="budget-qualified"
                  checked={editedLead.budget_qualified}
                  onCheckedChange={(checked) => setEditedLead({ ...editedLead, budget_qualified: checked })}
                />
              </div>
            </div>
            <div>
              <Label>Property Address</Label>
              <Input
                value={editedLead.property_address}
                onChange={(e) => setEditedLead({ ...editedLead, property_address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>City</Label>
                <Input
                  value={editedLead.property_city}
                  onChange={(e) => setEditedLead({ ...editedLead, property_city: e.target.value })}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={editedLead.property_state}
                  onChange={(e) => setEditedLead({ ...editedLead, property_state: e.target.value })}
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={editedLead.property_zip}
                  onChange={(e) => setEditedLead({ ...editedLead, property_zip: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t">
          {editMode ? (
            <>
              <Button onClick={handleSaveLead} className="flex-1">
                <Save className="h-3 w-3 mr-1" />
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEmailDialogOpen(true)}
                disabled={!lead.contact_email}
              >
                <Mail className="h-3 w-3 mr-1" />
                Send Email (AI)
              </Button>
              {!lead.converted_to_job_id && lead.status !== "lost" && (
                <Button
                  size="sm"
                  className="bg-action hover:bg-action-hover"
                  onClick={() => onConvert(lead.id)}
                  disabled={isConverting}
                >
                  <ArrowRight className="h-3 w-3 mr-1" />
                  Convert to Job
                </Button>
              )}
              {lead.converted_to_job_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/jobs/${lead.converted_to_job_id}`)}
                >
                  View Job
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
        <TabsList className="w-full justify-start border-b rounded-none px-6 bg-background">
          <TabsTrigger value="overview">
            <BarChart3 className="h-3 w-3 mr-1" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="h-3 w-3 mr-1" />
            Activity ({activities.length})
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-3 w-3 mr-1" />
            Team & Activity
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <CheckCircle className="h-3 w-3 mr-1" />
            Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="campaigns">
            <Target className="h-3 w-3 mr-1" />
            Campaigns ({enrollments.length})
          </TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1 px-6">
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* AI Intelligence */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    AI Lead Intelligence
                  </CardTitle>
                  {!leadScore && (
                    <Button size="sm" variant="outline" onClick={handleCalculateScore} disabled={calculatingScore}>
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {calculatingScore ? "Calculating..." : "Calculate Score"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {leadScore ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Overall Score</span>
                        <Badge className={cn("text-lg font-bold px-3 py-1", getScoreColor(leadScore.overall_score))}>
                          {leadScore.overall_score}/100
                        </Badge>
                      </div>
                      <Progress value={leadScore.overall_score} className="h-2" />
                    </div>
                    {leadScore.ai_recommendation && (
                      <div className="flex items-start gap-2 p-4 bg-primary/5 rounded-lg border border-primary/10">
                        <Lightbulb className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold text-sm text-primary mb-1">AI Recommendation</div>
                          <p className="text-sm text-primary/90">{leadScore.ai_recommendation}</p>
                        </div>
                      </div>
                    )}
                    <Separator />
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Property Value Score</div>
                        <div className="flex items-center gap-2">
                          <Progress value={(leadScore.score_breakdown?.property_value / 25) * 100} className="h-1.5 flex-1" />
                          <span className="text-sm font-bold">{leadScore.score_breakdown?.property_value || 0}/25</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Engagement Score</div>
                        <div className="flex items-center gap-2">
                          <Progress value={(leadScore.score_breakdown?.engagement / 30) * 100} className="h-1.5 flex-1" />
                          <span className="text-sm font-bold">{leadScore.score_breakdown?.engagement || 0}/30</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Recency Score</div>
                        <div className="flex items-center gap-2">
                          <Progress value={(leadScore.score_breakdown?.recency / 20) * 100} className="h-1.5 flex-1" />
                          <span className="text-sm font-bold">{leadScore.score_breakdown?.recency || 0}/20</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">Source Quality</div>
                        <div className="flex items-center gap-2">
                          <Progress value={(leadScore.score_breakdown?.source_quality / 20) * 100} className="h-1.5 flex-1" />
                          <span className="text-sm font-bold">{leadScore.score_breakdown?.source_quality || 0}/20</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium mb-1">No AI score calculated yet</p>
                    <p className="text-xs">Click "Calculate Score" to analyze this lead with AI</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sales Intelligence */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Pain Points & Needs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {lead.pain_points ? (
                    <p className="text-sm whitespace-pre-wrap">{lead.pain_points}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No pain points identified yet</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Competitive Landscape
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {lead.competitors ? (
                    <p className="text-sm whitespace-pre-wrap">{lead.competitors}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No competitor information</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Notes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Sales Notes
                  </CardTitle>
                  {!editingNotes ? (
                    <Button variant="ghost" size="sm" onClick={() => setEditingNotes(true)}>
                      <Edit2 className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setEditingNotes(false);
                        setNotes(lead.notes || "");
                      }}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveNotes} disabled={savingNotes}>
                        <Save className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingNotes ? (
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add your sales notes here..."
                    rows={8}
                    className="resize-none"
                  />
                ) : (
                  <p className="text-sm whitespace-pre-wrap">
                    {notes || <span className="text-muted-foreground italic">No notes yet. Click Edit to add notes.</span>}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-3 mt-4">
            {activities.length === 0 ? (
              <Card className="p-12">
                <div className="text-center text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">No activity yet</p>
                  <p className="text-xs mt-1">Email and engagement activity will appear here</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-2">
                {activities.map((activity) => (
                  <Card key={activity.id} className="p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{activity.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                        {activity.metadata?.reply_body && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-md border">
                            <p className="text-xs font-semibold mb-1.5 text-muted-foreground">Reply Content:</p>
                            <p className="text-sm">{activity.metadata.reply_body}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create New Task
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Task Title *</Label>
                  <Input
                    placeholder="e.g., Follow up call, Send proposal, Schedule demo"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={newTaskDueDate}
                      onChange={(e) => setNewTaskDueDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={newTaskPriority} onValueChange={setNewTaskPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleAddTask} disabled={addingTask} className="w-full">
                  <Plus className="h-3 w-3 mr-1" />
                  {addingTask ? "Creating..." : "Create Task"}
                </Button>
              </CardContent>
            </Card>

            {tasks.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Active Tasks</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleCompleteTask(task.id)}
                        disabled={task.status === 'completed'}
                      >
                        <div className={cn(
                          "h-4 w-4 rounded-full border-2 flex items-center justify-center",
                          task.status === "completed" 
                            ? "bg-green-500 border-green-500" 
                            : "border-muted-foreground"
                        )}>
                          {task.status === "completed" && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                      </Button>
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-sm font-medium truncate",
                          task.status === "completed" && "line-through text-muted-foreground"
                        )}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {task.due_date && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                          {task.priority && (
                            <Badge className={cn("text-xs", getPriorityColor(task.priority))}>
                              {task.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="p-12">
                <div className="text-center text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">No tasks yet</p>
                  <p className="text-xs mt-1">Create your first task above to get started</p>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-3 mt-4">
            {enrollments.length === 0 ? (
              <Card className="p-12">
                <div className="text-center text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">Not enrolled in any campaigns</p>
                  <p className="text-xs mt-1">Enroll this lead in campaigns from the Sales Hub</p>
                </div>
              </Card>
            ) : (
              enrollments.map((enrollment) => (
                <Card key={enrollment.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold">{enrollment.campaign?.name || "Campaign"}</p>
                    </div>
                    <Badge variant={enrollment.status === "active" ? "default" : "secondary"}>
                      {enrollment.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {enrollment.sequence?.name || "Email Sequence"}
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <div>
                      <span className="text-muted-foreground">Current Step:</span>
                      <span className="ml-1 font-medium">{enrollment.current_step}</span>
                    </div>
                    {enrollment.next_send_at && (
                      <div>
                        <span className="text-muted-foreground">Next Send:</span>
                        <span className="ml-1 font-medium">
                          {new Date(enrollment.next_send_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Team & Activity Tab */}
          <TabsContent value="team" className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Team Collaboration
              </h3>
              <ApprovalRequestDialog 
                leadId={leadId} 
                orgId={lead?.org_id || ""}
              >
                <Button variant="outline" size="sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Request Approval
                </Button>
              </ApprovalRequestDialog>
            </div>

            <CollaborationFeed 
              leadId={leadId} 
              orgId={lead?.org_id || ""} 
            />

            <div className="border-t pt-4">
              <CommentComposer 
                leadId={leadId} 
                orgId={lead?.org_id || ""} 
              />
            </div>
          </TabsContent>

        </ScrollArea>
      </Tabs>

      {/* Email Composer Dialog with AI */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email to {lead.contact_name}</DialogTitle>
            <DialogDescription>
              Describe the email you want and let AI draft it, then tweak before sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI Email Assistant
                </CardTitle>
                <CardDescription className="text-xs">
                  Example: "Follow-up email after no response", "Intro email for new storm lead", "Quote follow-up".
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Label>What kind of email do you need?</Label>
                <Input
                  placeholder="Describe the email you want Bridgette to write..."
                  value={aiEmailPrompt}
                  onChange={(e) => setAiEmailPrompt(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  className="mt-2"
                  disabled={draftingWithAI || !aiEmailPrompt.trim()}
                  onClick={async () => {
                    if (!aiEmailPrompt.trim()) return;
                    setDraftingWithAI(true);
                    try {
                      const { data, error } = await supabase.functions.invoke('llm-router', {
                        body: {
                          user_input: `Write a ${aiEmailPrompt} sales email to ${lead.contact_name || 'this homeowner'} about their roofing project at ${lead.property_address || 'their property'}. Make it clear, friendly, and concise. Do not add subject line unless explicitly requested.`,
                          org_id: orgId,
                          user_id: user?.id,
                        },
                      });

                      if (error) throw error;

                      const draft = data?.response || data?.spoken_response;
                      if (draft) {
                        setEmailBody(draft);
                        toast.success("AI draft ready – review and customize before sending.");
                      } else {
                        toast.error("AI did not return a draft. Try rephrasing the request.");
                      }
                    } catch (err) {
                      console.error('AI draft error:', err);
                      toast.error("Failed to generate email with AI");
                    } finally {
                      setDraftingWithAI(false);
                    }
                  }}
                >
                  {draftingWithAI ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Drafting...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3 mr-1" />
                      Draft with AI
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <div>
              <Label>Subject Line *</Label>
              <Input
                placeholder="Email subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                placeholder="Compose or edit your email..."
                rows={12}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              <Send className="h-3 w-3 mr-1" />
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

